import GraphQLClient from 'ts/GraphQLClient'
import { ApiResponse, RepoParams } from 'ts/types'
import {
  TimeCard,
  UnallocatedAccount,
  UpdateTimeCardStatusParams,
  UsageStats,
  UsageStatsParams,
  UsageStatsResponse
} from '../types'
import { GET_TIME_CARDS, GET_UNALLOCATED_ACCOUNTS, GET_USAGE_STATS, UPDATE_TIME_CARD_STATUS } from './graphql'

export default class SummaryRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, GRAPHQL_API_KEY }: RepoParams) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, apiKey: GRAPHQL_API_KEY })
  }

  async getUsageStats(params: UsageStatsParams): Promise<ApiResponse<UsageStats>> {
    const { year, token, month } = params

    try {
      const response = await this.client.request<{ getUsageStats: ApiResponse<UsageStats> }>({
        query: GET_USAGE_STATS,
        variables: { token, year, month }
      })

      return response.getUsageStats
    } catch (error) {
      throw new Error(`Failed to fetch usage stats: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getTimeCards(params: UsageStatsParams): Promise<ApiResponse<TimeCard[]>> {
    const { year, token, month } = params

    try {
      const response = await this.client.request<{ getTimeCardsForUser: ApiResponse<TimeCard[]> }>({
        query: GET_TIME_CARDS,
        variables: { token, year, month }
      })
      return response.getTimeCardsForUser
    } catch (error) {
      throw new Error(`Failed to fetch time cards: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  async getUnallocatedAccounts(params: UsageStatsParams): Promise<ApiResponse<UnallocatedAccount[]>> {
    const { year, token, month } = params

    try {
      const response = await this.client.request<{ getUnallocatedAccounts: ApiResponse<UnallocatedAccount[]> }>({
        query: GET_UNALLOCATED_ACCOUNTS,
        variables: { token, year, month }
      })
      return response.getUnallocatedAccounts
    } catch (error) {
      throw new Error(
        `Failed to fetch unallocated accounts: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }

  // Optional: Keep a combined method for backward compatibility
  async getAllSummaryData(params: UsageStatsParams): Promise<UsageStatsResponse> {
    const [usageStats, timeCards, unallocatedAccounts] = await Promise.all([
      this.getUsageStats(params),
      this.getTimeCards(params),
      this.getUnallocatedAccounts(params)
    ])

    return {
      getUsageStats: usageStats,
      getTimeCardsForUser: timeCards,
      getUnallocatedAccounts: unallocatedAccounts
    }
  }

  async updateTimeCardStatus(params: UpdateTimeCardStatusParams): Promise<ApiResponse<boolean>> {
    const { token, id, status } = params

    try {
      const response = await this.client.request<{ updateTimeCardStatus: ApiResponse<boolean> }>({
        query: UPDATE_TIME_CARD_STATUS,
        variables: { token, id, status }
      })

      return response.updateTimeCardStatus
    } catch (error) {
      throw new Error(`Failed to update time card status: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }
}
